import { OpponentHand } from "@/components/game/opponent-hand";
// ...existing code...
import { PlayedCardsSection } from "@/components/game/played-cards-section";
import { CardSlots } from "@/components/game/card-slots";
import { EnhancedPlayerAvatar } from "@/components/game/enhanced-player-avatar";
import { useEffect, useRef, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { PlayerHand } from "@/components/game/player-hand";
import { GameBoard } from "@/components/game/game-board";
import { useAuth } from "@/hooks/use-auth";
import { Loader2, Users, Swords } from "lucide-react";
import { Button } from "@/components/ui/button";
import useGameNotifications from "@/hooks/use-game-notifications";
import { GameNotifications } from "@/components/ui/game-notifications";
import { Capacitor } from "@capacitor/core";
import { GameScoreHeader } from "@/components/game/game-score-header";
import { GameSettingsMenu } from "@/components/game/game-settings-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { apiRequest } from "@/lib/queryClient";
import { gameRooms, type GameRoom } from "@shared/schema";
import { toast } from "@/hooks/use-toast";
import { PlayerList } from "@/components/game/player-list";
import { AnimatePresence, motion } from "framer-motion";
import { MobileHeader } from "@/components/ui/mobile-header";
import { EnhancedHeader } from "@/components/ui";
import { useLocalDecks } from "@/hooks/use-local-decks";
import { USE_LOCAL_ASSETS } from "@/config/constants";

// Import our app sound service
import appSound from '@/services/app-sound-service';
import { audioCache } from "@/services/audio-cache";
import { GameTutorial } from "@/components/tutorial/GameTutorial";
import { useTutorialSettings } from "@/hooks/useTutorialSettings";
// Import sound files from the new soundlist
import { defaultSounds } from "@/components/game/soundlist";
import { Badge } from "@/components/ui/badge";
import { useDefaultDeck } from "@/hooks/use-default-deck";
import { gameSettings } from "@shared/game-settings";

// Game notification type
type GameNotification = {
  id: string;
  title: string;
  message: string;
  type: "info" | "warning" | "error";
  timeout?: number;
};

type GameState = {
  players: Array<{ id: number; username: string }>;
  currentTurn: number;
  playerCards: string[];
  player1PlayedCards: string[];
  player2PlayedCards: string[];
  scores: { [key: number]: number };
  round: number;
  lastPlayed?: {
    player: number;
    card: string;
    isLeading: boolean;
  };
  isRoundEnded: boolean;
  isMatchEnded: boolean;
  status: "waiting" | "playing" | "ended";
  cardsDealt: boolean;
  connectedPlayers: number[];
  isSpectator: boolean;
  spectators?: number[];
  gameWinner?: number;
  isGameEnded?: boolean;
  opponentCardCount: number; // Add this to keep track of opponent's card count
  playerCardCounts: { [key: number]: number }; // Add this to track card count for each player
  gameSettings?: [];
};

const INITIAL_GAME_STATE: GameState = {
  players: [],
  currentTurn: 0,
  playerCards: [],
  player1PlayedCards: [],
  player2PlayedCards: [],
  scores: {},
  round: 1,
  isRoundEnded: false,
  isMatchEnded: false,
  isGameEnded: false,
  status: "waiting",
  cardsDealt: false,
  connectedPlayers: [],
  isSpectator: false,
  spectators: [],
  gameWinner: undefined,
  opponentCardCount: 5, // Initialize with 5 cards (full hand)
  playerCardCounts: {}, // Initialize playerCardCounts
  gameSettings: []

};

const CONNECTION_TIMEOUT = 8000; // 8 seconds
const RECONNECT_BASE_DELAY = 2000; // 2 seconds
const MAX_RECONNECT_DELAY = 10000; // 10 seconds
const JITTER = 1000; // Random delay up to 1 second to prevent thundering herd
const MAX_RECONNECT_ATTEMPTS = 3;

// --- Responsive Layout Section Height Plan ---
// This logic will be used to apportion vertical space for each main section of the game room.
// All heights are in percentages of viewport height (vh) and sum to 100vh (no scroll).
//
// Section height allocation:
// - Header: 8vh
// - Opponent Hand: 12vh
// - Opponent Avatar: 8vh
// - Played Cards Section: 25vh
// - Under 10 Button: 6vh (if visible)
// - Player Hand: 25vh
// - Player Avatar: 8vh
// - Padding/Margins: 8vh (remaining space for gaps and padding)

const SECTION_HEIGHTS = {
  header: 8,
  opponentHand: 15,
  opponentAvatar: 10,
  playedCards: 24,
  under10Button: 6, // Only if visible
  playerHand: 15,
  playerAvatar: 10,
  padding: 12, // Remaining space for gaps and margins
};

// Utility to get total height for a set of sections
function getSectionHeight(...sections: (keyof typeof SECTION_HEIGHTS)[]) {
  return sections.reduce((sum, key) => sum + SECTION_HEIGHTS[key], 0);
}

export default function GameRoom() {
  const [match, params] = useRoute<{ id: string }>("/game/:id");
  const [_, setLocation] = useLocation();
  const { user, token } = useAuth();
  const [gameState, setGameState] = useState<GameState>(INITIAL_GAME_STATE);
  const [error, setError] = useState<string | null>(null);
  const [showTutorial, setShowTutorial] = useState(false);
  // Notification system
  const { notifications, showNotification, removeNotification } =
    useGameNotifications();

  const {
    isTutorialCompleted,
    isTutorialDisabled,
    settings,
    markTutorialComplete,
    disableTutorial,
  } = useTutorialSettings();

  // Default deck query
  const defaultDeckQuery = useDefaultDeck();
  
  // Local decks hook for asset resolution
  const { getDeckFromUrl } = useLocalDecks();

  // Preload all game sounds
  useEffect(() => {
    // Preload all common game sounds using the audioCache service
    const soundsToPreload = [
      { key: "gameplay.cardPlace", url: defaultSounds["gameplay.cardPlace"] },
      { key: "gameplay.cardFlip", url: defaultSounds["gameplay.cardFlip"] },
      { key: "gameplay.cardDeal", url: defaultSounds["gameplay.cardDeal"] },
      { key: "ui.shuffle", url: defaultSounds["ui.shuffle"] },
      { key: "ui.toggle", url: defaultSounds["ui.toggle"] },
      { key: "win.game", url: defaultSounds["win.game"] },
      { key: "lose.game", url: defaultSounds["lose.game"] },
      { key: "gameplay.roundEnd", url: defaultSounds["gameplay.roundEnd"] },
      { key: "gameplay.turnChange", url: defaultSounds["gameplay.turnChange"] },
    ];

    // Preload each sound using the audioCache service
    soundsToPreload.forEach(({ key, url }) => {
      if (url) {
        audioCache.preloadAudio(key, url).catch((error) => {
          console.warn(`Failed to preload sound ${key}:`, error);
        });
      }
    });

    // When entering game room, tell our sound service
    appSound.enterGameRoom();

    // Cleanup when leaving game room
    return () => {
      appSound.exitGameRoom();
    };
  }, []);

  // Get the game room data and other state before useEffect runs
  const { data: gameRoom, isLoading: isLoadingGame } = useQuery<GameRoom>({
    queryKey: ["game-room", params?.id],
    queryFn: async () => {
      if (!params?.id) throw new Error("No game ID provided");
      const res = await apiRequest("GET", `/api/game-rooms/${params.id}`);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to fetch game room");
      }
      return res.json();
    },
    retry: false,
    enabled: !!params?.id && !!user,
  });

  // Board background query
  const { data: boardBackground } = useQuery({
    queryKey: ["board-background"],
    queryFn: async () => {
      try {
        const res = await apiRequest("GET", "/api/game-boards/current");
        if (!res.ok) return null;
        return res.json();
      } catch (error) {
        console.error("Error fetching board background:", error);
        return null;
      }
    },
    enabled: !!token,
    // Add these options to ensure background refreshes
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 0
  });

  // Reset tutorial settings - for debugging
  // Commented out to fix issue with tutorial showing for every practice match
  /*
  useEffect(() => {
    // This will force reset the tutorial settings when a new game starts
    // Comment this out when not debugging tutorial issues
    if (typeof localStorage !== "undefined") {
      console.log("Resetting tutorial settings for debugging");
      try {
        localStorage.removeItem("lead-card-tutorial-settings");
        localStorage.removeItem(
          "lead-card-tutorial-settings-game-basics-completed",
        );
      } catch (e) {
        console.error("Error resetting tutorial settings:", e);
      }
    }
  }, []);
  */
  

  // Check if we should show the tutorial (only in practice mode against AI)
  useEffect(() => {
    // Skip this effect if the tutorial has been marked as completed and we're not in a testing mode
    if (isTutorialCompleted("game-basics")) {
      console.log("Tutorial already completed, skipping");
      return;
    }

    // Also skip if tutorial is disabled by user
    if (isTutorialDisabled("game-basics")) {
      console.log("Tutorial disabled by user, skipping");
      return;
    }

    console.log("Game state updated:", {
      status: gameState.status,
      players: gameState.players,
      playerCount: gameState.players.length,
      currentTurn: gameState.currentTurn,
      cardsDealt: gameState.cardsDealt,
      playerCards: gameState.playerCards.length,
    });

    // Look for conditions to determine this is a practice game
    if (user && gameState.players.length > 0) {
      // Check if this is a practice game (against AI)
      // Practice games will usually:
      // 1. Have "Practice" in the game room name
      // 2. Have only two players
      // 3. Often have an AI player with negative ID or "AI"/"Bot" in the name
      const isPracticeGame = gameRoom?.name?.includes("Practice") || false;

      // Any player whose username contains AI, Bot, or has a negative ID is considered an AI
      const aiPlayers = gameState.players.filter(
        (p) =>
          (p.username &&
            (p.username.includes("AI") ||
              p.username.includes("Bot") ||
              p.username.includes("Computer"))) ||
          p.id < 0,
      );

      // If we're in a practice game or we detected an AI player, show the tutorial
      const isAgainstAI = isPracticeGame || aiPlayers.length > 0;

      console.log("Tutorial eligibility:", {
        isPracticeGame,
        aiPlayers,
        isAgainstAI,
        gameRoomName: gameRoom?.name,
        cardsDealt: gameState.cardsDealt,
        playerCardsAvailable: gameState.playerCards.length > 0,
        tutorialAlreadyCompleted: isTutorialCompleted("game-basics"),
        tutorialDisabled: isTutorialDisabled("game-basics"),
      });

      // More aggressive tutorial showing approach:
      // 1. It's a practice game or against AI
      // 2. We have a game room name, which means the game is connected
      // 3. Tutorial hasn't been completed or disabled
      if (
        isAgainstAI &&
        gameRoom?.name && // Game room exists and connected
        !isTutorialCompleted("game-basics") &&
        !isTutorialDisabled("game-basics")
      ) {
        console.log("TUTORIAL SHOULD SHOW - Practice game detected");

        // Don't flash the tutorial on and off - only set it once
        if (!showTutorial) {
          // Show tutorial with even more delay to ensure all DOM elements are ready
          const showTutorialWithDelay = () => {
            console.log("Showing tutorial now!");
            setShowTutorial(true);
          };

          // A much longer delay to ensure all DOM elements are ready
          setTimeout(showTutorialWithDelay, 1000);
        }
      }
    }
  }, [
    gameState,
    user,
    gameRoom,
    showTutorial,
    isTutorialCompleted,
    isTutorialDisabled,
  ]);

  // Removed selectedCard and setSelectedCard; PlayerHand will manage its own selection
  const [isConnecting, setIsConnecting] = useState(true);
  const wsRef = useRef<WebSocket | null>(null);
  const connectionTimeoutRef = useRef<NodeJS.Timeout>();
  const [connectedPlayers, setConnectedPlayers] = useState<Set<number>>(
    new Set(),
  );
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  // Add state for tracking if we recently had a Gbaa violation
  const [hadGbaaViolation, setHadGbaaViolation] = useState(false);

  // Under 10 feature states
  const [isUnder10, setIsUnder10] = useState(false);
  const [under10ButtonShown, setUnder10ButtonShown] = useState(false);

  // Calculate if current player is player1
  const getCurrentPlayerPosition = () => {
    return user?.id === gameState.players[0]?.id;
  };

  // Check if the player has an Under 10 hand
  useEffect(() => {
    // Only check for Under 10 when cards are dealt and the player has cards
    if (
      gameState.cardsDealt &&
      gameState.playerCards.length === 5 &&
      !gameState.isSpectator
    ) {
      // Check if all cards are under 10 (values 6-9)
      const hasUnder10Deck = gameState.playerCards.every((card) => {
        const value = parseInt(card.substring(1));
        return value < 10;
      });

      setIsUnder10(hasUnder10Deck);

      // Only show the button if the player has an Under 10 deck and it's their turn
      // and no cards have been played yet
      if (hasUnder10Deck && gameState.currentTurn === user?.id) {
        const isPlayerOne = getCurrentPlayerPosition();
        const playerPlayedCards = isPlayerOne
          ? gameState.player1PlayedCards
          : gameState.player2PlayedCards;

        // Only show the button if no cards have been played yet
        setUnder10ButtonShown(playerPlayedCards.length === 0);

        // Notify the player with a game notification if they have an Under 10 deck
        if (playerPlayedCards.length === 0) {
          showNotification(
            "Under 10 Deck!",
            "You can replace two cards before playing",
            "info",
            5000,
          );
        }
      } else {
        setUnder10ButtonShown(false);
      }
    } else {
      setIsUnder10(false);
      setUnder10ButtonShown(false);
    }
  }, [
    gameState.cardsDealt,
    gameState.playerCards,
    gameState.currentTurn,
    user?.id,
    gameState.player1PlayedCards,
    gameState.player2PlayedCards,
    gameState.players,
  ]);

  // Handler for Under 10 card replacement
  const handleUnder10Replace = () => {
    if (!wsRef.current || !user || !token || !params?.id) return;

    // Mark user interaction and play replace cards sound
    audioCache.markUserInteraction();
    appSound.playReplaceCardsSound();

    wsRef.current.send(
      JSON.stringify({
        type: "UNDER_10_REPLACE",
        gameId: parseInt(params.id),
        token,
      }),
    );

    // Hide the button after using it
    setUnder10ButtonShown(false);
  };

  useEffect(() => {
    if (!gameRoom || !user || !token || !params?.id) return;

    // Immediately redirect if game is completed
    if (gameRoom.isCompleted) {
      if (gameState.isSpectator) {
        setLocation("/watch");
      } else {
        setLocation("/games");
      }
      return;
    }

    // Only create WebSocket connection if game is not completed
    let ws: WebSocket | null = null;
    let reconnectTimeout: NodeJS.Timeout;
    let connectionTimeout: NodeJS.Timeout;
    let reconnectAttempts = 0;

    const connect = () => {
      if (ws?.readyState === WebSocket.OPEN) {
        console.log("WebSocket already connected");
        return;
      }

      setIsConnecting(true);
      setError(null);

      // Clear any existing timeouts
      clearTimeout(connectionTimeoutRef.current);
      clearTimeout(reconnectTimeoutRef.current);

      // Create WebSocket URL with Capacitor-aware logic
      let wsUrl;
      // Check if we're running in a Capacitor native app
      if (Capacitor.isNativePlatform()) {
        // For native apps, we need to use the server URL from environment variables
        const serverUrl =
          import.meta.env.VITE_SERVER_URL || window.location.origin;
        const parsedUrl = new URL(serverUrl);
        wsUrl = `${parsedUrl.protocol === "https:" ? "wss:" : "ws:"}//${parsedUrl.host}/ws`;
        console.log("Mobile WebSocket - using absolute URL:", wsUrl);
      } else {
        // For web, use relative path which automatically adapts to the current host
        const wsProtocol =
          window.location.protocol === "https:" ? "wss:" : "ws:";
        wsUrl = `${wsProtocol}//${window.location.host}/ws`;
        console.log("Web WebSocket - using relative URL:", wsUrl);
      }

      // Ensure we have the default deck URL before connecting
      const defaultDeckUrl = defaultDeckQuery.data?.deckUrl;
      
      // Use local assets instead of remote deck URL if enabled
      let deckUrlToUse = defaultDeckUrl;
      if (USE_LOCAL_ASSETS && defaultDeckUrl) {
        // For local assets, we just need the original URL for deck identification
        // The actual card loading will use local paths via the Card component
        deckUrlToUse = defaultDeckUrl; // Keep original for server-side deck identification
        console.log("Using local assets - deck URL for server:", deckUrlToUse);
      }

      console.log("Connecting to game with default deck:", deckUrlToUse);

      // Include the default deck URL in the WebSocket connection params
      const wsParams = new URLSearchParams({
        token: token,
        deckUrl: defaultDeckUrl,
      }).toString();

      ws = new WebSocket(`${wsUrl}?${wsParams}`);

      connectionTimeout = setTimeout(() => {
        if (ws?.readyState !== WebSocket.OPEN) {
          console.log("Connection timeout, attempting reconnect");
          ws?.close();
          handleReconnect();
        }
      }, CONNECTION_TIMEOUT);

      ws.onopen = () => {
        console.log("WebSocket connected");
        clearTimeout(connectionTimeout);
        setIsConnecting(false);
        setError(null);
        reconnectAttempts = 0;

        ws?.send(
          JSON.stringify({
            type: "JOIN_GAME",
            gameId: parseInt(params.id),
            token,
          }),
        );
      };

      ws.onclose = (event) => {
        console.log("WebSocket closed", event);
        clearTimeout(connectionTimeout);
        setIsConnecting(false);

        // Don't attempt to reconnect if we just had a Gbaa violation
        if (hadGbaaViolation) {
          console.log("Skipping reconnect due to recent Gbaa rule violation", {
            code: event.code,
            reason: event.reason,
          });
          setHadGbaaViolation(false); // Reset for future connections
          return;
        }

        // Prevent unneeded reconnection attempts for clean closes
        if (event.wasClean) {
          console.log("WebSocket closed cleanly, no reconnect needed");
          return;
        }

        handleReconnect();
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        clearTimeout(connectionTimeout);
        setIsConnecting(false);
        setError("Disconnected from the game.");
        handleReconnect();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          switch (data.type) {
            case "GBAA_RULE_VIOLATION":
              // Set the Gbaa violation flag to prevent reconnection on WebSocket close
              //setHadGbaaViolation(true);

              // Log this to help debugging
              console.log(
                "Gbaa rule violation detected, setting hadGbaaViolation flag",
              );

              // Find and remove any temporary card being played class
              const cardBeingPlayed =
                document.querySelector(".card-being-played");
              if (cardBeingPlayed) {
                cardBeingPlayed.classList.remove("card-being-played");
                // Add a visual indication this was an invalid move
                cardBeingPlayed.classList.add("card-invalid-move");
                // Remove the invalid move class after a short delay
                setTimeout(() => {
                  cardBeingPlayed.classList.remove("card-invalid-move");
                }, 1000);
              }

              // Make the game container border glow red for one second
              const gameContainer = document.getElementById("game-container");
              if (gameContainer) {
                gameContainer.classList.add("gbaa-violation");
                setTimeout(() => {
                  gameContainer.classList.remove("gbaa-violation");
                }, 1000);
              }

              // Use our custom notification system instead of toast
              //showNotification("Gbaa Rule", data.message, "error", 5000);

              // If the server explicitly asks to keep the connection alive
              if (data.keepAlive) {
                console.log(
                  "Server requested to keep connection alive after Gbaa violation",
                );
                // Make sure we acknowledge this was a special keep-alive situation
                if (ws && ws.readyState === WebSocket.OPEN) {
                  ws.send(
                    JSON.stringify({
                      type: "GBAA_ACKNOWLEDGE",
                      gameId: parseInt(params?.id || "0"),
                      token,
                    }),
                  );
                }
              }
              break;
            case "MATCH_FOUND":
              // Show match acceptance dialog
              const acceptDialog = document.createElement("div");
              acceptDialog.id = "match-dialog";
              acceptDialog.style.cssText = `
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.8);
                z-index: 99999;
                display: flex;
                align-items: center;
                justify-content: center;
              `;

              const dialogContent = document.createElement("div");
              dialogContent.className =
                "bg-background p-6 rounded-lg shadow-xl max-w-md w-full mx-4";
              acceptDialog.appendChild(dialogContent);

              // Define the updateDialogContent function as a const expression
              const updateDialogContent = (acceptedPlayers: number[] = []) => {
                if (!dialogContent) return;

                dialogContent.innerHTML = `
                  <h2 class="text-xl font-bold mb-4">Match Found!</h2>
                  <div class="mb-4">
                    <div class="flex items-center gap-2 mb-2">
                      <div class="w-2 h-2 rounded-full ${user && acceptedPlayers.includes(user.id) ? "bg-green-500" : "bg-yellow-500"}"></div>
                      <span>Waiting for players to accept... (${acceptedPlayers.length}/2)</span>
                    </div>
                  </div>
                  <div class="flex gap-4 justify-end">
                    ${
                      user && !acceptedPlayers.includes(user.id)
                        ? `<button id="accept" class="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors">Accept</button>
                       <button id="reject" class="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors">Reject</button>`
                        : `<button disabled class="px-6 py-2 bg-gray-600 text-white rounded-md">Waiting...</button>`
                    }
                  </div>
                `;
              };

              document.body.appendChild(acceptDialog);
              updateDialogContent();

              const acceptBtn = acceptDialog.querySelector("#accept");
              const rejectBtn = acceptDialog.querySelector("#reject");

              acceptBtn?.addEventListener("click", () => {
                wsRef.current?.send(
                  JSON.stringify({
                    type: "ACCEPT_MATCH",
                    userId: user?.id,
                    token: data.token,
                  }),
                );
              });

              rejectBtn?.addEventListener("click", () => {
                wsRef.current?.send(
                  JSON.stringify({
                    type: "REJECT_MATCH",
                    userId: user?.id,
                    token: data.token,
                  }),
                );
                document.body.removeChild(acceptDialog);
              });
              break;

            case "MATCH_ACCEPTANCE_UPDATE":
              const dialog = document.getElementById("match-dialog");
              if (dialog) {
                const dialogContent = dialog.querySelector(".bg-background");
                if (dialogContent) {
                  // Define the update function inline to avoid scope issues
                  const updateDialogContent = (
                    acceptedPlayers: number[] = [],
                  ) => {
                    if (dialogContent) {
                      dialogContent.innerHTML = `
                        <h2 class="text-xl font-bold mb-4">Match Found!</h2>
                        <div class="mb-4">
                          <div class="flex items-center gap-2 mb-2">
                            <div class="w-2 h-2 rounded-full ${user && acceptedPlayers.includes(user.id) ? "bg-green-500" : "bg-yellow-500"}"></div>
                            <span>Waiting for players to accept... (${acceptedPlayers.length}/2)</span>
                          </div>
                        </div>
                        <div class="flex gap-4 justify-end">
                          ${
                            user && !acceptedPlayers.includes(user.id)
                              ? `<button id="accept" class="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md transition-colors">Accept</button>
                                <button id="reject" class="px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors">Reject</button>`
                              : `<button disabled class="px-6 py-2 bg-gray-600 text-white rounded-md">Waiting...</button>`
                          }
                        </div>
                      `;
                    }
                  };

                  // Ensure acceptedBy is an array of numbers
                  const acceptedPlayers = Array.isArray(data.acceptedBy)
                    ? data.acceptedBy
                    : [];
                  updateDialogContent(acceptedPlayers);
                }
              }
              break;

            case "MATCH_ACCEPTED":
              const matchDialog = document.getElementById("match-dialog");
              if (matchDialog) {
                document.body.removeChild(matchDialog);
              }
              setLocation(`/game/${data.gameId}`);
              break;
            case "ERROR":
              if (data.redirect) {
                setLocation(data.redirect);
                return;
              }
              if (
                data.message.includes("Invalid token") ||
                data.message.includes("expired token")
              ) {
                showNotification(
                  "Session Expired",
                  "Please log in again",
                  "error",
                  5000,
                );
                setLocation("/auth");
              } else if (
                //data.message.includes("Gbaa") ||
                //data.message.includes("gbaa") ||
                //data.message.includes("Wrong card")
                data.message.includes("Wrong card")
              ) {
                // Set the Gbaa violation flag to prevent reconnection on WebSocket close
                setHadGbaaViolation(true);

                // Log this to help debugging
                console.log(
                  "Gbaa-related error detected in ERROR message, setting hadGbaaViolation flag",
                  { message: data.message },
                );

                // Use custom notification instead of toast
                showNotification("Gbaa Rule", data.message, "error", 5000);

                // If the server explicitly asks to keep the connection alive
                if (data.keepAlive) {
                  console.log(
                    "Server requested to keep connection alive after Gbaa error",
                  );
                  break;
                } else {
                  // Don't trigger reconnection for this specific error
                  // Continue processing the message but don't return immediately
                  // This gives the WebSocket stack time to process other events
                  console.log(
                    "No keepAlive flag, but still avoiding reconnection",
                  );
                }
              } else if (data.message.includes("Not your turn")
              ){
                
                //showNotification("Error", "It's not your turn to play.", "error", 1000);
              } else if (data.message.includes("Invalid card - not in your hand")
              ){
                
                //showNotification("Error", "It's not your turn to play.", "error", 1000);
              } else {
                showNotification("Error", data.message, "error", 5000);
                
                
              }
              break;
            case "GAME_STATE":
              // Calculate card counts for all players
              const playerCardCounts: { [key: number]: number } = {};
              if (data.state.cardsDealt) {
                data.state.players.forEach((player: { id: number }) => {
                  const isPlayer1 = player.id === data.state.players[0]?.id;
                  const playedCards = isPlayer1 
                    ? data.state.player1PlayedCards || []
                    : data.state.player2PlayedCards || [];
                  
                  // Full hand (5) minus played cards
                  playerCardCounts[player.id] = 5 - playedCards.length;
                });
              }

              // Update state
              setGameState(prevState => ({
                ...prevState,
                ...data.state,
                // Preserve local state
                playerCards: data.state.playerCards || prevState.playerCards,
                currentTurn: data.state.currentTurn || prevState.currentTurn,
                players: data.state.players || prevState.players,
                scores: data.state.scores || prevState.scores,
                // Set card counts for all players
                playerCardCounts,
                // Keep opponentCardCount for backward compatibility
                //opponentCardCount: playerCardCounts[data.state.players.find(p => p.id !== user?.id)?.id] || 0,
              }));
              setError(null);

              // Play sounds only if relevant state changed
              if (data.state.isRoundEnded && !gameState.isRoundEnded) {
                appSound.playRoundWinSound();
              } else if (data.state.currentTurn === user?.id && gameState.currentTurn !== user?.id) {
                appSound.playCardPlayedSound();
              }
              
              // Check for win conditions
              if (data.state.gameWinner && data.state.gameWinner === user?.id && !gameState.gameWinner) {
                appSound.playGameWinSound();
              }
              break;

            case "CARD_PLAYED":
              // Update played cards and decrement opponent count if applicable
              setGameState(prevState => {
                const isMyCardPlay = data.playerId === user?.id;
                const updatedPlayerCardCounts = { ...prevState.playerCardCounts };
                
                // Decrement the card count for the player who played
                if (updatedPlayerCardCounts[data.playerId] > 0) {
                  updatedPlayerCardCounts[data.playerId]--;
                }

                return {
                  ...prevState,
                  player1PlayedCards: data.player1Cards || prevState.player1PlayedCards,
                  player2PlayedCards: data.player2Cards || prevState.player2PlayedCards,
                  currentTurn: data.nextTurn || prevState.currentTurn,
                  lastPlayed: data.lastPlayed || prevState.lastPlayed,
                  playerCardCounts: updatedPlayerCardCounts,
                  // Keep opponentCardCount updated for backward compatibility
                  //opponentCardCount: updatedPlayerCardCounts[prevState.players.find(p => p.id !== user?.id)?.id] || 0,
                };
              });

              // Play card played sound
              appSound.playCardPlayedSound();
              break;

            case "PLAYER_CONNECTED":
              setConnectedPlayers(
                (prev) => new Set([...Array.from(prev), data.playerId]),
              );
              if (!data.isSpectator) {
                //toast({
                //title: "Player Connected",
                //description: `${data.username} joined the game`,
                //});
              }
              break;
            case "PLAYER_DISCONNECTED":
              setConnectedPlayers((prev) => {
                const newSet = new Set(Array.from(prev));
                newSet.delete(data.playerId);
                return newSet;
              });
              if (!data.isSpectator) {
                //toast({
                //title: "Player Disconnected",
                //description: `${data.username} left the game`,
                //variant: "destructive",
                //});
              }
              break;

            case "UNDER_10_REPLACED":
              // Play replace cards sound
              appSound.playReplaceCardsSound();

              // Use our custom notification system instead of toast
              showNotification(
                "Cards Replaced",
                data.isCurrentPlayer
                  ? "You've replaced two cards using the Under 10 rule!"
                  : `${data.username} replaced cards using the Under 10 rule`,
                "info",
                5000,
              );
              break;
          }
        } catch (err) {
          console.error("Error parsing WebSocket message:", err);
        }
      };
    };

    const handleReconnect = () => {
      if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        setError("Could not establish connection. Please refresh the page.");
        return;
      }

      reconnectAttempts++;
      console.log(
        `Attempting reconnect ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS}`,
      );

      const delay = Math.min(
        RECONNECT_BASE_DELAY * Math.pow(2, reconnectAttempts) +
          Math.random() * JITTER,
        MAX_RECONNECT_DELAY,
      );

      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, delay);
    };

    connect();
    wsRef.current = ws;

    return () => {
      console.log("Cleaning up WebSocket connection");
      // Clear all timeouts
      //clearTimeout(reconnectTimeout);
      clearTimeout(connectionTimeout);
      clearTimeout(reconnectTimeoutRef.current);
      clearTimeout(connectionTimeoutRef.current);

      // If user is spectator, send leave message before closing
      if (
        gameState.isSpectator &&
        wsRef.current?.readyState === WebSocket.OPEN
      ) {
        wsRef.current.send(
          JSON.stringify({
            type: "LEAVE_AS_SPECTATOR",
            gameId: parseInt(params?.id || "0"),
            token,
          }),
        );
      }

      // Close and cleanup WebSocket connections
      if (ws) {
        ws.onclose = null; // Prevent reconnect attempts
        ws.onerror = null;
        ws.close();
        ws = null;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent reconnect attempts
        wsRef.current.onerror = null;
        wsRef.current.close();
        wsRef.current = null;
      }

      // Reset connection state
      setIsConnecting(false);
      setReconnectAttempts(0);
    };
  }, [gameRoom, params?.id, user, token]);

  // Remove handleCardSelect and handleCardPlay; PlayerHand will handle selection and playing

  // New callback for when a card is played
  const handleCardPlay = (card: string) => {
    if (!card || !wsRef.current || !user || !token || !params?.id) return;
    // Play card played sound
    appSound.playCardPlayedSound();
    wsRef.current.send(
      JSON.stringify({
        type: "PLAY_CARD",
        gameId: parseInt(params.id),
        token,
        payload: { card },
      }),
    );
  };

  const handleNewRound = () => {
    if (!wsRef.current || !user || !token || !params?.id) return;

    // Play start new round sound
    appSound.playStartNewRoundSound();

    wsRef.current.send(
      JSON.stringify({
        type: "NEW_ROUND",
        gameId: parseInt(params.id),
        token,
      }),
    );
  };

  const handleDealCards = () => {
    if (!wsRef.current || !token || !params?.id) return;

    // Play deal cards sound
    appSound.playDealCardsSound();

    wsRef.current.send(
      JSON.stringify({
        type: "DEAL_CARDS",
        gameId: parseInt(params.id),
        token,
      }),
    );
  };

  const isPlayer1 = user?.id === gameState.players[0]?.id;

  if (!user) {
    setLocation("/auth");
    return null;
  }

  if (isLoadingGame || isConnecting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-emerald-950 via-gray-900 to-black">
        <div className="relative w-16 h-16">
          <Loader2 className="h-16 w-16 animate-spin text-primary/40" />
          <Swords className="h-8 w-8 text-primary absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="text-white/80 mt-4">
          {isLoadingGame ? "Loading game..." : "Connecting to game..."}
        </p>
      </div>
    );
  }

  // Redirect if game is completed
  if (gameRoom?.isCompleted) {
    if (gameState.isSpectator) {
      setLocation("/watch");
    } else {
      setLocation("/games");
    }
    return null;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen themed-gradient gap-4">
        <div className="text-red-500 text-xl font-semibold">{error}</div>
        <Button onClick={() => window.location.reload()}>
          Retry Connection
        </Button>
        <Button variant="outline" onClick={() => setLocation("/games")}>
          Return to Games
        </Button>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 text-white overflow-hidden flex flex-col"
      id="game-container"
      style={{
        backgroundImage: boardBackground?.board ? `url(${encodeURI(boardBackground.board.imageUrl)})` : 'linear-gradient(to bottom right, rgb(6 78 59), rgb(17 24 39), rgb(0 0 0))',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        height: '100vh',
      }}
    >
      {/* Semi-transparent overlay to ensure cards are visible */}
      <div className="absolute inset-0 bg-black/40 themed-gradient opacity-5 pointer-events-none"></div>

      {/* Game notification system */}
      <GameNotifications
        notifications={notifications}
        removeNotification={removeNotification}
      />

      {/* Tutorial component */}
      {params?.id && (
        <GameTutorial
          gameId={parseInt(params.id)}
          isVisible={showTutorial}
          onComplete={() => {
            console.log("Tutorial completed");
            markTutorialComplete("game-basics");
            setShowTutorial(false);
          }}
          onSkip={() => {
            console.log("Tutorial skipped");
            markTutorialComplete("game-basics");
            disableTutorial("game-basics");
            setShowTutorial(false);
          }}
        />
      )}

      {/* Game header with scoreboard - Fixed height section */}
      <div 
        style={{ height: `${SECTION_HEIGHTS.header}vh`, minHeight: 0 }} 
        className="flex-shrink-0 flex flex-col justify-center"
      >
        <EnhancedHeader
          title={`${gameRoom?.gameType === "ai" ? "Practice" : gameRoom?.gameType}`}
          backPath="/games"
          showTitle={false}
          showBackButton={true}
          className="flex justify-center"
          rightContent={
            <div className="flex items-center gap-2">
              <GameScoreHeader 
                gameRoom={gameRoom} 
                gameState={gameState}
                user={user}
              />
              <GameSettingsMenu />
            </div>
          }
        />
      </div>

      {/* Main vertical stack for game sections */}
      <div className="relative flex-1 flex flex-col w-full max-w-2xl mx-auto z-10 justify-between" style={{ minHeight: 0 }}>
        {/* Opponent hand card backs - Fixed height section */}
        {!gameState.isSpectator && gameState.players.length > 1 && (
          <div 
            style={{ height: `${SECTION_HEIGHTS.opponentHand}vh`, minHeight: 0 }} 
            className="flex items-center justify-center w-full flex-shrink-0"
          >
            {(() => {
              const opponent = gameState.players.find((player: { id: number }) => player.id !== user?.id);
              const opponentId = opponent?.id;
              return (
                <OpponentHand
                  numCards={opponentId !== undefined ? gameState.playerCardCounts[opponentId] || 0 : 0}
                  playerId={opponentId}
                  noAnimation={false}
                  className="transform-origin-top"
                />
              );
            })()}
          </div>
        )}

        {/* Opponent hand for spectator view - Fixed height section */}
        {gameState.isSpectator && gameState.players.length > 1 && (
          <div 
            style={{ height: `${SECTION_HEIGHTS.opponentHand}vh`, minHeight: 0 }} 
            className="flex items-center justify-center w-full flex-shrink-0"
          >
            <OpponentHand
              numCards={gameState.playerCardCounts[gameState.players[1]?.id] || 0}
              playerId={gameState.players[1]?.id}
              noAnimation={false}
              className="transform-origin-top"
            />
          </div>
        )}

        {/* Top Avatars (opponent or spectator view) - Fixed height section */}
        <div 
          style={{ height: `${SECTION_HEIGHTS.opponentAvatar}vh`, minHeight: 0 }} 
          className="flex justify-center items-center flex-shrink-0"
        >
          {gameState.isSpectator ? (
            gameState.players[1] && (
              <EnhancedPlayerAvatar
                key={gameState.players[1].id}
                player={gameState.players[1]}
                showControls={false}
                isOpponent={true}
                isConnected={gameState.connectedPlayers?.includes(gameState.players[1].id)}
                currentTurn={gameState.currentTurn}
                scores={gameState.scores}
                playerCardCounts={gameState.playerCardCounts}
                cardsDealt={gameState.cardsDealt}
                isRoundEnded={gameState.isRoundEnded}
                gameSettings={gameRoom?.gameSettings as { timer: { timerEnabled: boolean; timerDuration: number; timerWarning: number; }; } | undefined}
              />
            )
          ) : (
            gameState.players
              .filter((player) => player.id !== user?.id)
              .map((player) => (
                <EnhancedPlayerAvatar
                  key={player.id}
                  player={player}
                  showControls={true}
                  isOpponent={true}
                  isConnected={gameState.connectedPlayers?.includes(player.id)}
                  currentTurn={gameState.currentTurn}
                  scores={gameState.scores}
                  playerCardCounts={gameState.playerCardCounts}
                  cardsDealt={gameState.cardsDealt}
                  isRoundEnded={gameState.isRoundEnded}
                  gameSettings={gameRoom?.gameSettings as { timer: { timerEnabled: boolean; timerDuration: number; timerWarning: number; }; } | undefined}
                />
              ))
          )}
        </div>

        {/* Played Cards Section - Fixed height section, centered between avatars */}
        <div 
          style={{ height: `${SECTION_HEIGHTS.playedCards}vh`, minHeight: 0 }} 
          className="flex items-center justify-center flex-shrink-0"
        >
          <PlayedCardsSection
            opponentCards={gameState.isSpectator
              ? gameState.player2PlayedCards
              : isPlayer1
                ? gameState.player2PlayedCards
                : gameState.player1PlayedCards}
            playerCards={gameState.isSpectator
              ? gameState.player1PlayedCards
              : isPlayer1
                ? gameState.player1PlayedCards
                : gameState.player2PlayedCards}
            isOpponent={true}
            opponentId={gameState.players.find((player) => player.id !== user?.id)?.id}
            isRoundEnded={gameState.isRoundEnded}
            isMatchEnded={gameState.isMatchEnded}
            isHost={gameRoom?.creator === user?.id}
            cardsDealt={gameState.cardsDealt}
            onDealCards={handleDealCards}
            onStartNewRound={handleNewRound}
            under10ButtonShown={under10ButtonShown}
            isUnder10={isUnder10}
            isCurrentTurn={gameState.currentTurn === user?.id}
            onUnder10Replace={handleUnder10Replace}
            gameRoom={{
              lastRoundWinner:
                gameRoom?.lastRoundWinner !== null
                  ? gameRoom?.lastRoundWinner
                  : undefined,
              name: gameRoom?.name,
            }}
            isSpectator={gameState.isSpectator}
          />
        </div>

        {/* Under 10 button - Fixed height section (only when visible) */}
        {isUnder10 && under10ButtonShown && gameState.currentTurn === user?.id && (
          <div 
            style={{ height: `${SECTION_HEIGHTS.under10Button}vh`, minHeight: 0 }} 
            className="flex flex-col items-center justify-center flex-shrink-0"
          >
            <div className="bg-emerald-600/90 text-white font-semibold text-sm py-1 px-3 rounded mb-2 animate-pulse">
              Under 10 Deck!
            </div>
            <Button
              onClick={handleUnder10Replace}
              variant="default"
              className="bg-primary hover:bg-primary/90 text-white shadow-lg animate-bounce"
            >
              Replace 2 Cards
            </Button>
          </div>
        )}

        {/* Bottom Avatars (current player) - Fixed height section */}
        {!gameState.isSpectator && (
          <div 
            style={{ height: `${SECTION_HEIGHTS.playerAvatar}vh`, minHeight: 0 }} 
            className="flex justify-center items-center flex-shrink-0"
          >
            {gameState.players
              .filter((player) => player.id === user?.id)
              .map((player) => (
                <EnhancedPlayerAvatar
                  key={player.id}
                  player={player}
                  showControls={true}
                  isConnected={gameState.connectedPlayers?.includes(player.id)}
                  currentTurn={gameState.currentTurn}
                  scores={gameState.scores}
                  playerCardCounts={gameState.playerCardCounts}
                  cardsDealt={gameState.cardsDealt}
                  isRoundEnded={gameState.isRoundEnded}
                  gameSettings={gameRoom?.gameSettings as { timer: { timerEnabled: boolean; timerDuration: number; timerWarning: number; }; } | undefined}
                />
              ))}
          </div>
        )}
        

        {/* Player Hand - Fixed height section (only for non-spectators) */}
        {!gameState.isSpectator && (
          <div 
            style={{ height: `${SECTION_HEIGHTS.playerHand}vh`, minHeight: 0 }} 
            className="flex items-center justify-center flex-shrink-0"
          >
            <PlayerHand
              cards={gameState.playerCards}
              isCurrentTurn={gameState.currentTurn === user?.id}
              isUnder10={isUnder10}
              onUnder10Replace={handleUnder10Replace}
              under10ButtonShown={under10ButtonShown}
              onCardPlay={handleCardPlay}
            />
          </div>
        )}

        {/* Spectator bottom avatar area and opponent hand - Fixed height sections */}
        {gameState.isSpectator && (
          <>
            <div 
              style={{ height: `${SECTION_HEIGHTS.playerAvatar}vh`, minHeight: 0 }} 
              className="flex justify-center items-center flex-shrink-0"
            >
              {gameState.players[0] && (
                <EnhancedPlayerAvatar
                  key={gameState.players[0].id}
                  player={gameState.players[0]}
                  showControls={false}
                  isConnected={gameState.connectedPlayers?.includes(gameState.players[0].id)}
                  currentTurn={gameState.currentTurn}
                  scores={gameState.scores}
                  playerCardCounts={gameState.playerCardCounts}
                  cardsDealt={gameState.cardsDealt}
                  isRoundEnded={gameState.isRoundEnded}
                  gameSettings={gameRoom?.gameSettings as { timer: { timerEnabled: boolean; timerDuration: number; timerWarning: number; }; } | undefined}
                />
              )}
            </div>
            {/* Opponent hand for spectator at bottom (player 0) */}
            {gameState.players.length > 1 && gameState.playerCardCounts[gameState.players[0]?.id] > 0 && (
              <div 
                style={{ height: `${SECTION_HEIGHTS.opponentHand}vh`, minHeight: 0 }} 
                className="flex items-center justify-center w-full flex-shrink-0"
              >
                <OpponentHand
                  numCards={gameState.playerCardCounts[gameState.players[0]?.id] || 0}
                  playerId={gameState.players[0]?.id}
                  noAnimation={false}
                  className="transform-origin-top"
                  isOpponentState={true}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

